import { useEffect, useRef, useState } from "react";

const CSS = `
@keyframes bb-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
@keyframes bb-blink{0%,92%,100%{transform:scaleY(1)}95%{transform:scaleY(.1)}}
@keyframes bb-wave{0%,100%{transform:rotate(-150deg)}50%{transform:rotate(-115deg)}}
@keyframes bb-cheerL{0%,100%{transform:rotate(155deg)}50%{transform:rotate(140deg)}}
@keyframes bb-cheerR{0%,100%{transform:rotate(-155deg)}50%{transform:rotate(-140deg)}}
@keyframes bb-swingL{0%,100%{transform:rotate(10deg)}50%{transform:rotate(5deg)}}
@keyframes bb-swingR{0%,100%{transform:rotate(-10deg)}50%{transform:rotate(-5deg)}}
@keyframes bb-talk{0%,100%{transform:scaleY(1)}50%{transform:scaleY(2.6)}}
@keyframes bb-think{0%,100%{transform:translateX(-1.5px)}50%{transform:translateX(1.5px)}}
@keyframes bb-shm{0%,100%{transform:skewX(0)}50%{transform:skewX(1.5deg)}}
.bb-root{animation:bb-float 3.2s ease-in-out infinite}
.bb-eye{transform-box:fill-box;transform-origin:center;animation:bb-blink 4.2s infinite}
.bb-arm{transform-box:fill-box;transition:transform .45s cubic-bezier(.34,1.56,.64,1)}
.bb-armL{transform-origin:85% 8%}.bb-armR{transform-origin:15% 8%}
.bb-idle .bb-armL{animation:bb-swingL 3.2s ease-in-out infinite}.bb-idle .bb-armR{animation:bb-swingR 3.2s ease-in-out infinite}
.bb-wave .bb-armL{animation:bb-swingL 3.2s ease-in-out infinite}.bb-wave .bb-armR{animation:bb-wave .7s ease-in-out infinite}
.bb-cheer .bb-armL{animation:bb-cheerL .6s ease-in-out infinite}.bb-cheer .bb-armR{animation:bb-cheerR .6s ease-in-out infinite}
.bb-mouth{transform-box:fill-box;transform-origin:center}
.bb-talk .bb-mouth{animation:bb-talk .3s ease-in-out infinite}
.bb-think .bb-eyes{animation:bb-think 1s ease-in-out infinite}
.bb-drape{transform-box:fill-box;transform-origin:50% 0;animation:bb-shm 3.2s ease-in-out infinite}
`;

const SKIN = "#eac39c", BEARD = "#5a4e47", THOBE = "#fbfbfa", LINE = "#e2e2de";

// Baraa: Saudi gentleman in white thobe, red shemagh and black iqal; eyes follow the cursor.
export const BaraaBot = ({ size = 64, mood = "idle", headOnly = false, track = true, className = "" }) => {
  const ref = useRef(null);
  const [eye, setEye] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (!track) return undefined;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height * 0.3);
        const d = Math.max(1, Math.hypot(dx, dy));
        setEye({ x: (dx / d) * Math.min(1.4, d / 80), y: (dy / d) * Math.min(1, d / 100) });
      });
    };
    window.addEventListener("pointermove", onMove);
    return () => { window.removeEventListener("pointermove", onMove); cancelAnimationFrame(raf); };
  }, [track]);

  const vb = headOnly ? "22 2 76 76" : "0 0 120 150";
  return (
    <span ref={ref} className={`inline-block ${className}`} style={{ width: size, height: headOnly ? size : size * 1.25 }} data-mood={mood}>
      <style>{CSS}</style>
      <svg viewBox={vb} width="100%" height="100%" className={`bb-${mood} ${headOnly ? "" : "bb-root"}`} aria-hidden="true">
        <defs>
          <pattern id="bb-shm" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="5" height="5" fill="#ffffff" />
            <rect width="2.5" height="2.5" fill="#d61f26" /><rect x="2.5" y="2.5" width="2.5" height="2.5" fill="#d61f26" />
          </pattern>
          <linearGradient id="bb-thobe" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#ecebe7" /><stop offset=".5" stopColor={THOBE} /><stop offset="1" stopColor="#ecebe7" />
          </linearGradient>
        </defs>
        {!headOnly && (
          <g>
            <g className="bb-arm bb-armL"><rect x="14" y="76" width="16" height="44" rx="8" fill="url(#bb-thobe)" stroke={LINE} /><circle cx="22" cy="121" r="6.5" fill={SKIN} /></g>
            <g className="bb-arm bb-armR"><rect x="90" y="76" width="16" height="44" rx="8" fill="url(#bb-thobe)" stroke={LINE} /><circle cx="98" cy="121" r="6.5" fill={SKIN} /></g>
            <path d="M26 80 Q30 68 60 66 Q90 68 94 80 L98 138 Q98 148 88 148 L32 148 Q22 148 22 138 Z" fill="url(#bb-thobe)" stroke={LINE} />
            <path d="M52 70 Q60 76 68 70" stroke="#d9d8d3" strokeWidth="2" fill="none" strokeLinecap="round" />
            <line x1="60" y1="74" x2="60" y2="104" stroke="#e4e3df" strokeWidth="1.4" />
            <circle cx="60" cy="82" r="1.3" fill="#cfcec9" /><circle cx="60" cy="92" r="1.3" fill="#cfcec9" />
            <rect x="70" y="86" width="12" height="2" rx="1" fill="#e4e3df" />
          </g>
        )}
        <rect x="52" y="58" width="16" height="12" rx="4" fill="#d9ad84" />
        <path className="bb-drape" d="M31 50 Q28 6 60 5 Q92 6 89 50 L98 96 Q84 100 74 86 Q66 74 60 72 Q54 74 46 86 Q36 100 22 96 Z" fill="url(#bb-shm)" stroke="#b91c1c" strokeOpacity=".35" />
        <ellipse cx="60" cy="46" rx="17.5" ry="21" fill={SKIN} />
        <ellipse cx="42.5" cy="46" rx="2.4" ry="4" fill="#dcae86" /><ellipse cx="77.5" cy="46" rx="2.4" ry="4" fill="#dcae86" />
        <path d="M43 46 Q43 67 60 69 Q77 67 77 46 Q74 57 67 57.5 Q60 55 53 57.5 Q46 57 43 46 Z" fill={BEARD} />
        <path d="M51 53.5 Q60 49.5 69 53.5 Q60 55.5 51 53.5 Z" fill={BEARD} />
        <ellipse className="bb-mouth" cx="60" cy="57.6" rx="3.6" ry="1.2" fill="#7f1d1d" />
        <path d="M48 37.5 Q52 35.5 56 37.3" stroke="#3f342e" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <path d="M64 37.3 Q68 35.5 72 37.5" stroke="#3f342e" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <path d="M59 42 Q57.5 48 60 48.5" stroke="#c99a72" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        <g className="bb-eyes" style={{ transform: `translate(${eye.x}px, ${eye.y}px)`, transition: "transform .12s ease-out" }}>
          <ellipse className="bb-eye" cx="52" cy="41.5" rx="2.3" ry="2.6" fill="#2a211c" />
          <ellipse className="bb-eye" cx="68" cy="41.5" rx="2.3" ry="2.6" fill="#2a211c" />
          <circle cx="52.8" cy="40.7" r=".7" fill="#fff" /><circle cx="68.8" cy="40.7" r=".7" fill="#fff" />
        </g>
        <path d="M38 34 Q60 22 82 34 L83 28 Q60 15 37 28 Z" fill="url(#bb-shm)" stroke="#b91c1c" strokeOpacity=".4" />
        <path d="M36 22 Q60 9 84 22" stroke="#141414" strokeWidth="3.4" fill="none" strokeLinecap="round" />
        <path d="M36.5 27 Q60 14 83.5 27" stroke="#141414" strokeWidth="3.4" fill="none" strokeLinecap="round" />
      </svg>
    </span>
  );
};
